import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import useToggle from "../../../hooks/useToggle";
import { useI18n } from "../../../hooks/useTranslations";
import useApi from "../../../http/api";
import { useRequestDocumentApi } from "../../../http/processingRequestDocument/api";
import { ProcessingRequestDocument, RenameRequestDocument } from "../../../http/processingRequestDocument/types";
import { PaginationQuery } from "../../../types";
import { isImage, isPdf, isVideo } from "../../../utils";
import SvgDelete from "../../icons/Delete";
import SvgDownload from "../../icons/Download";
import SvgEdit from "../../icons/Edit";
import SvgPaperClip from "../../icons/PaperClip";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import Input from "../../ui/Input";
import { Action } from "../../ui/ListItemMenu";
import ModalDialog from "../../ui/ModalDialog";
import PreviewFileDialog from "../../ui/PreviewFileDialog";
import Sidebar from "../../ui/Sidebar";
import Table from "../../ui/Table";
import InfoCell from "./InfoCell";
import styles from "./RequestDocuments.module.css";
import UploadRequestDocumentButton from "./UploadDocumentButton";

type RequestDocumentsProps = {
  requestID: number;
};

const RequestDocuments: FC<RequestDocumentsProps> = ({ requestID }) => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const dispatch = useContext(NotificationDispatch);

  const { isOpen, toggle, close } = useToggle();
  const [deleteDocument, setDeleteDocument] = useState<ProcessingRequestDocument>();
  const [renameDocument, setRenameDocument] = useState<ProcessingRequestDocument>();
  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);

  const api = useApi();

  const documentsApi = useRequestDocumentApi();

  const downloadDocument = async (docuent: ProcessingRequestDocument) =>
    api
      .downloadFile(`/api/processing_request_documents/${docuent.id}`, docuent.name)
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const { data, error } = documentsApi.useRequestDocuments(query, requestID);

  const onRenameSubmit = async (formData: RenameRequestDocument) => {
    try {
      if (renameDocument) {
        await documentsApi.renameProcessingRequestDocument(
          { name: formData.name + renameDocument.name.slice(renameDocument.name.lastIndexOf(".")) },
          renameDocument.id,
          requestID,
        );
      }
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
      return;
    }
    setRenameDocument(undefined);
  };

  const getDocumentUrl = (document: ProcessingRequestDocument) =>
    `/api/processing_request_documents/${document.id}` + `?t=${new Date().getTime()}`;

  const onPreviewClick = async (document: ProcessingRequestDocument) => {
    if (isVideo(document.name) || isImage(document.name) || isPdf(document.name)) {
      const docUrl = await api.createObjectURL(getDocumentUrl(document), true);
      setItemToPreview({ name: document.name, url: docUrl });
      return;
    }
    api.downloadFile(getDocumentUrl(document), document.name);
  };

  const actions: Action<ProcessingRequestDocument>[] = [
    {
      title: i18n.t.common.download,
      onClick: (document) => {
        downloadDocument(document);
      },
      icon: SvgDownload,
    },
    {
      title: i18n.t.common.rename,
      onClick: (document: ProcessingRequestDocument) => {
        setRenameDocument(document);
      },
      icon: SvgEdit,
    },
    {
      title: i18n.t.common.delete,
      onClick: (document: ProcessingRequestDocument) => {
        setDeleteDocument(document);
      },
      color: "red" as const,
      icon: SvgDelete,
      isDivider: true,
    },
  ];

  const onDocumentClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, document: ProcessingRequestDocument) => {
    e.preventDefault();
    onPreviewClick(document);
    return false;
  };

  return (
    <>
      <Sidebar title={i18n.t.documents.plural} isOpen={isOpen} onClose={() => close()}>
        <Table<ProcessingRequestDocument>
          buttons={{
            main: <UploadRequestDocumentButton requestId={requestID} />,
          }}
          head={
            <tr>
              <th align="left">{i18n.t.common.name}</th>
              <th></th>
            </tr>
          }
          renderRow={(document) => (
            <>
              <td>
                <a
                  className={styles.fileName}
                  onClick={(e) => onDocumentClick(e, document)}
                  href={getDocumentUrl(document)}
                >
                  {document.name}
                </a>
              </td>
            </>
          )}
          rowActions={actions}
          items={data?.items}
          total={data?.total}
          error={error}
        />
      </Sidebar>
      <InfoCell
        title={i18n.t.documents.plural + ": " + (data?.total ?? 0).toString()}
        sideIcon={SvgPaperClip}
        onClick={toggle}
      ></InfoCell>
      {!!deleteDocument && (
        <ModalDialog
          onClose={() => setDeleteDocument(undefined)}
          title={i18n.t.documents.delete}
          isSubmitting={isModalSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setDeleteDocument(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                setIsModalSubmitting(true);
                if (!deleteDocument) return;
                try {
                  await documentsApi.deleteProcessingRequestDocument(deleteDocument.id, requestID);
                  setIsModalSubmitting(false);
                  setDeleteDocument(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.documents.questions.delete}
        </ModalDialog>
      )}
      {!!renameDocument && (
        <ModalDialog onClose={() => setRenameDocument(undefined)} title={i18n.t.documents.rename}>
          <Form<RenameRequestDocument>
            defaultValues={{ name: renameDocument.name.slice(0, renameDocument.name.lastIndexOf(".")) }}
            submitText={i18n.t.common.save}
            onSubmit={() => onRenameSubmit}
          >
            {({ control }) => {
              return (
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: i18n.t.common.fieldCannotBeEmpty }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.common.name} error={fieldState.error}>
                      {({ labelId, isInvalid }) => <Input id={labelId} {...field} isInvalid={isInvalid} />}
                    </FormField>
                  )}
                />
              );
            }}
          </Form>
        </ModalDialog>
      )}
      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}
    </>
  );
};

export default RequestDocuments;
